import React from 'react';
import { Provider } from 'react-redux';
import ReactDOM from 'react-dom/client';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider } from './context/ThemeContext';
import { WhiteLabelProvider } from './context/WhiteLabelContext';

import './index.scss';
import App from './App';
import reportWebVitals from './reportWebVitals';
import store from './redux/store';
import { config } from './config';
import { checkDomainAccess, createRequestHeaders } from './utils/domainUtils';

// In production, silence debug-level console output (log/debug/info) so internal
// logs don't leak to end users. console.warn and console.error are kept — real
// problems must still surface. CRA sets NODE_ENV=production in the build, so this
// is a no-op in dev where the logs are useful.
if (process.env.NODE_ENV === 'production') {
  const noop = () => {};
  console.log = noop;
  console.debug = noop;
  console.info = noop;
}

// Domain restriction check is now handled by centralized utility

// Fetch white label configuration to get the correct Google client ID
const getWhiteLabelConfig = async () => {
  try {
    const hostname = window.location.hostname;

    // Skip API call for localhost/development domains
    if (hostname === 'localhost' || hostname.startsWith('localhost:') ||
        hostname === '127.0.0.1' || hostname.startsWith('127.0.0.1:')) {
      return null;
    }

    const headers = createRequestHeaders();

    const response = await fetch(`${config.API_URI}/v1/white-label/branding`, {
      method: 'GET',
      credentials: 'include',
      headers: headers,
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.error('Error fetching white label config:', error);
  }

  return null;
};

const initializeApp = async () => {

  const hasAccess = await checkDomainAccess();

  if (!hasAccess) {
    // Redirect to the main Zoobbe domain, NOT config.DOMAIN which equals
    // window.location.origin on whitelabel domains — that would cause
    // an infinite reload loop (redirecting to the same page).
    window.location.href = 'https://zoobbe.com';
    return;
  }

  // Fetch white label configuration to determine the correct Google client ID
  const whiteLabelConfig = await getWhiteLabelConfig();

  // Determine Google OAuth configuration
  let googleClientId = config.GOOGLE_CLIENT_ID; // Default Zoobbe client ID

  if (whiteLabelConfig && whiteLabelConfig.isWhiteLabel) {
    // For white label domains, check if Google OAuth is enabled and has custom client ID
    if (whiteLabelConfig.oauth?.enableGoogleAuth && whiteLabelConfig.oauth?.googleClientId) {
      googleClientId = whiteLabelConfig.oauth.googleClientId;
    } 
  } 

  const root = ReactDOM.createRoot(document.getElementById('root'));

  // Always wrap with GoogleOAuthProvider to prevent hook errors
  // The actual OAuth functionality is controlled by the WhiteLabelContext
  const AppWithProviders = () => (
    <Provider store={store}>
      <GoogleOAuthProvider clientId={googleClientId}>
        <WhiteLabelProvider>
          <ThemeProvider> 
            <App />
          </ThemeProvider>
        </WhiteLabelProvider>
      </GoogleOAuthProvider>
    </Provider>
  );

  root.render(<AppWithProviders />);
};

// Eliminate the "ResizeObserver loop completed with undelivered notifications"
// warning AT THE SOURCE: wrap the native ResizeObserver so its callback runs on
// the next animation frame instead of synchronously. Coalescing the callback to
// rAF means a resize triggered inside a resize observation is batched into the
// next frame rather than re-entering the same delivery cycle — which is exactly
// the condition that produces the "undelivered notifications" loop. This is a
// real fix (the loop never forms), not just hiding the message. All RO consumers
// (virtualizer measurement, Board/PageList/etc. resize watchers) benefit
// uniformly, and observe/unobserve/disconnect are passed straight through.
if (typeof window !== 'undefined' && window.ResizeObserver) {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class RafResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      let scheduled = false;
      let lastArgs = null;
      super((entries, observer) => {
        lastArgs = [entries, observer];
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(() => {
          scheduled = false;
          if (lastArgs) callback(lastArgs[0], lastArgs[1]);
        });
      });
    }
  };
}

// Belt-and-suspenders: still suppress the benign "ResizeObserver loop" warning
// in case any observer was created before the wrapper above. It is non-fatal —
// the spec defers extra notifications to the next frame — but CRA's
// react-error-overlay treats any window error as fatal and shows a red overlay
// in dev. We filter ONLY this exact message; every other error passes through.
const RO_LOOP_MESSAGE = 'ResizeObserver loop completed with undelivered notifications';
const RO_LOOP_LEGACY = 'ResizeObserver loop limit exceeded';
const isResizeObserverLoopMessage = (msg) =>
  typeof msg === 'string' && (msg.includes(RO_LOOP_MESSAGE) || msg.includes(RO_LOOP_LEGACY));

const originalConsoleError = console.error;
console.error = (...args) => {
  if (isResizeObserverLoopMessage(args[0])) {
    return; // swallow only the ResizeObserver loop warning
  }
  originalConsoleError.apply(console, args); // everything else logs normally
};

// Stop the dev error overlay from surfacing it. We register in the CAPTURE
// phase and call stopImmediatePropagation so we run before — and prevent —
// react-error-overlay's own error listener. Only the RO loop message is caught.
window.addEventListener(
  'error',
  (event) => {
    if (isResizeObserverLoopMessage(event.message)) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  },
  true
);

initializeApp();



// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
